import { useEffect, useState } from 'react';

import { TSearchTypeFileds } from 'types/searchFields';

import { RequestResult } from '@api/responseModels/requestResult';
import { ISearchResp } from '@api/responseModels/search/searchResponse';
import { GlobalConstants } from '@constants/global';
import { store as pageSizeStore } from '@molecules/paginationControls/pageSizeStore';
import { apiRequest } from '@utils/apiRequest';

import { DEF_PAGINATION_PAGE, DEF_SEARCH_RESULT } from './constants';
import { searchTransportFromMap, searchTransportToMap } from './transports';
import { IUseSearchProps, IUseSearchReturn, TSearchResult } from './types';
import { useAdvancedDebounce } from './useAdvancedDeboounce';

const baseKeys: TSearchTypeFileds[] = ['base_product', 'base_service', 'base_task'];

export const useSearch = ({
    model,
    query,
    filters,
    sorts,
    pageSize,
    search_attributes,
    crmId: crmIdOverride,
}: IUseSearchProps): IUseSearchReturn => {
    const [pageNumber, setPageNumber] = useState(DEF_PAGINATION_PAGE);
    const [searchResult, setSearchResult] = useState<TSearchResult>(DEF_SEARCH_RESULT);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [debouncedSearch, flushSearch, cancelSearch] = useAdvancedDebounce(
        search,
        GlobalConstants.SearchTimeout
    );

    const cleanQuery = query?.trim();

    useEffect(() => {
        cleanQuery ? debouncedSearch() : resetSearch();
    }, [cleanQuery]);

    useEffect(() => {
        cleanQuery && flushSearch();
    }, [pageNumber]);

    useEffect(() => {
        if (pageNumber === 1) {
            cleanQuery && flushSearch();
        } else {
            setPageNumber(1);
        }
    }, [model, filters, sorts]);

    async function request() {
        const isBase = baseKeys.includes(model);
        return apiRequest(async (api, crmId) => {
            const crm_id = isBase ? undefined : crmIdOverride ?? crmId;
            return await api.search({
                crm_id,
                search_attributes,
                ...searchTransportToMap({
                    model,
                    query: cleanQuery,
                    filters,
                    sorts,
                    pageNumber,
                    pageSize: pageSize || pageSizeStore.pS,
                }),
            });
        });
    }

    function response(req: RequestResult<ISearchResp>): TSearchResult {
        // Ошибка запроса или ответ не того вида (бэк, у которого упал поиск,
        // отдаёт голый `[]` без `pagination`) — пустая выдача, а не падение экрана
        if (req?.errorMessages || !req?.data?.pagination || !Array.isArray(req.data.data)) {
            return DEF_SEARCH_RESULT;
        }

        const isSearchMore = req.data.pagination.page > 1;

        return {
            items: [...(isSearchMore ? searchResult.items : []), ...searchTransportFromMap(req)],
            pagination: req?.data.pagination,
            meta: {
                total_price: req?.data?.meta?.total_price || 0,
                total_quantity: req?.data?.meta?.total_quantity || req.data.pagination.total,
            },
            categories: req?.data.categories,
        };
    }

    async function search() {
        setIsSearchActive(true);
        setIsLoading(true);

        const req = await request();
        const res = response(req);

        setSearchResult(res);
        setIsLoading(false);
    }

    function searchMore() {
        setPageNumber(pageNumber + 1);
    }

    function searchFrom(newPageNumber: number) {
        resetSearch();
        setPageNumber(newPageNumber);
    }

    function resetSearch() {
        cancelSearch();
        setPageNumber(DEF_PAGINATION_PAGE);
        setSearchResult(DEF_SEARCH_RESULT);
        setIsSearchActive(false);
        setIsLoading(false);
    }

    return {
        isSearchActive,
        isLoading,
        searchResult,
        searchMore,
        searchFrom,
    };
};
